<template>
  <div class="cover-external-editor space-y-5">
    <!-- Switch mode -->
    <section class="cover-external-editor__section">
      <h4 class="cover-external-editor__section-title">{{ tt('Switch mode', 'Način stikala') }}</h4>
      <div class="cover-external-editor__mode-grid">
        <button
          v-for="option in MODE_OPTIONS"
          :key="option.value"
          type="button"
          class="cover-external-editor__mode"
          :class="{ 'cover-external-editor__mode--active': config.switch_mode === option.value }"
          @click="update({ switch_mode: option.value })">
          <ControlIcon :name="option.icon" size="1.125rem" />
          <span class="cover-external-editor__mode-label">{{ tt(option.label.en, option.label.sl) }}</span>
          <span class="cover-external-editor__mode-desc">{{ tt(option.desc.en, option.desc.sl) }}</span>
        </button>
      </div>
    </section>

    <!-- Positions -->
    <section class="cover-external-editor__section">
      <h4 class="cover-external-editor__section-title">{{ tt('Positions', 'Položaji') }}</h4>
      <div class="cover-external-editor__grid">
        <label class="cover-external-editor__number">
          <span>{{ tt('Open position', 'Položaj odprto') }}</span>
          <input
            type="number"
            min="0"
            max="100"
            :value="config.open_position"
            @change="update({ open_position: clampNum(Number(($event.target as HTMLInputElement).value), 0, 100) })" />
          <span>%</span>
        </label>
        <label class="cover-external-editor__number">
          <span>{{ tt('Close position', 'Položaj zaprto') }}</span>
          <input
            type="number"
            min="0"
            max="100"
            :value="config.close_position"
            @change="update({ close_position: clampNum(Number(($event.target as HTMLInputElement).value), 0, 100) })" />
          <span>%</span>
        </label>
      </div>
      <p class="cover-external-editor__hint">
        {{
          tt(
            '0 = fully open / retracted, 100 = fully closed. Bind the cover’s',
            '0 = popolnoma odprto / zloženo, 100 = popolnoma zaprto. Vrednost',
          )
        }}
        <b>{{ tt('position', 'položaja') }}</b>
        {{ tt('value as the output, and its', 'senčila povežite kot izhod, vrednost') }}
        <b>{{ tt('stop', 'stop') }}</b>
        {{
          tt(
            'value as the “Cover Stop” output for stop / hold-to-move modes.',
            'pa kot izhod »Cover Stop« za načina ustavi / drži za premik.',
          )
        }}
      </p>
    </section>

    <!-- Hold-to-move direction -->
    <section v-if="config.switch_mode === 'momentary'" class="cover-external-editor__section">
      <h4 class="cover-external-editor__section-title">{{ tt('Hold-to-move direction', 'Smer med držanjem') }}</h4>
      <div class="cover-external-editor__segmented">
        <button
          type="button"
          :class="{ 'cover-external-editor__segment--active': config.momentary_direction === 'open' }"
          @click="update({ momentary_direction: 'open' })">
          {{ tt('Open while held', 'Med držanjem odpira') }}
        </button>
        <button
          type="button"
          :class="{ 'cover-external-editor__segment--active': config.momentary_direction === 'close' }"
          @click="update({ momentary_direction: 'close' })">
          {{ tt('Close while held', 'Med držanjem zapira') }}
        </button>
      </div>
    </section>

    <!-- Debounce -->
    <section class="cover-external-editor__section">
      <h4 class="cover-external-editor__section-title">{{ tt('Debounce', 'Preprečevanje odboja') }}</h4>
      <label class="cover-external-editor__number">
        <span>{{ tt('Debounce time', 'Čas preprečevanja odboja') }}</span>
        <input
          type="number"
          min="0"
          max="500"
          step="10"
          :value="config.debounce_ms"
          @change="update({ debounce_ms: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })" />
        <span>ms</span>
      </label>
      <p class="cover-external-editor__hint">
        {{
          tt(
            'Rapid changes within this window are ignored (e.g. switch bounce).',
            'Hitre spremembe znotraj tega okna se prezrejo (npr. odboj stikala).',
          )
        }}
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  CoverExternalControlConfig,
  CoverExternalSwitchMode,
  normalizeCoverExternalControlConfig,
} from '../../../utils/coverAutomation';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';
import type { Bilingual } from '../../../config/bilingual';

const props = defineProps<{
  modelValue: Record<string, unknown>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, unknown>): void;
}>();

/**
 * The mode cards' words and drawn glyphs (card t_a8249a54): both languages in
 * the data, one picked at render; the icons are names of the drawn
 * `ControlIcon` set — a switch (`toggle`), a cycle (`refresh`), up/down
 * buttons (`up-down`) and a press (`tap`). `up_stop_down` shares
 * `open_stop_close`'s wording, as its English-only row already did; the two
 * are told apart by their description.
 */
const MODE_OPTIONS: Array<{
  value: CoverExternalSwitchMode;
  label: Bilingual;
  icon: string;
  desc: Bilingual;
}> = [
  {
    value: 'toggle',
    label: { en: 'Toggle', sl: 'Preklop' },
    icon: 'toggle',
    desc: { en: 'Single button', sl: 'En gumb' },
  },
  {
    value: 'open_stop_close',
    label: { en: 'Open / Stop / Close', sl: 'Odpri / Ustavi / Zapri' },
    icon: 'refresh',
    desc: { en: 'One button cycle', sl: 'En gumb v krogu' },
  },
  {
    value: 'up_down',
    label: { en: 'Up / Down', sl: 'Gor / Dol' },
    icon: 'up-down',
    desc: { en: 'Two buttons', sl: 'Dva gumba' },
  },
  {
    value: 'up_stop_down',
    label: { en: 'Open / Stop / Close', sl: 'Odpri / Ustavi / Zapri' },
    icon: 'up-down',
    desc: { en: 'Three buttons', sl: 'Trije gumbi' },
  },
  {
    value: 'momentary',
    label: { en: 'Hold to move', sl: 'Drži za premik' },
    icon: 'tap',
    desc: { en: 'Press & hold', sl: 'Pritisni in drži' },
  },
];

const config = computed<CoverExternalControlConfig>(() => normalizeCoverExternalControlConfig(props.modelValue));

function commit(next: CoverExternalControlConfig) {
  emit('update:modelValue', JSON.parse(JSON.stringify(next)) as Record<string, unknown>);
}

function update(patch: Partial<CoverExternalControlConfig>) {
  commit({ ...config.value, ...patch });
}

function clampNum(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
</script>

<style scoped>
.cover-external-editor {
  width: 100%;
}

.cover-external-editor__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgb(100, 116, 139);
  margin-bottom: 8px;
}

.cover-external-editor__hint {
  font-size: 11px;
  line-height: 1.45;
  color: rgb(148, 163, 184);
  margin-top: 6px;
}

.cover-external-editor__mode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.cover-external-editor__mode {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  text-align: left;
  transition: border-color 0.15s;
}

.cover-external-editor__mode--active {
  border-color: rgb(34, 197, 94);
  background: rgba(34, 197, 94, 0.08);
}

.cover-external-editor__mode--active .cover-external-editor__mode-label {
  color: rgb(22, 163, 74);
}

.cover-external-editor__mode-label {
  font-size: 12px;
  font-weight: 600;
  color: rgb(51, 65, 85);
}

.dark .cover-external-editor__mode-label {
  color: rgb(226, 232, 240);
}

.cover-external-editor__mode-desc {
  font-size: 10px;
  color: rgb(148, 163, 184);
}

.cover-external-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.cover-external-editor__number {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgb(148, 163, 184);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 6px 8px;
}

.cover-external-editor__number input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  text-align: right;
}

.cover-external-editor__segmented {
  display: inline-flex;
  gap: 4px;
  background: rgba(148, 163, 184, 0.12);
  border-radius: 999px;
  padding: 3px;
}

.cover-external-editor__segmented button {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 999px;
  color: rgb(148, 163, 184);
}

.cover-external-editor__segment--active {
  background: rgb(34, 197, 94);
  color: #fff !important;
}
</style>
